/**
 * convAiDebug.ts
 *
 * Local-only end-to-end tracing for the Conv-AI chat path. OFF by default;
 * enabled per dev-server run with
 *
 *     VITE_CONV_AI_DEBUG=true npm run dev
 *
 * It is DEV-gated as well as flag-gated, so a production bundle can never emit
 * these lines even if the variable were somehow defined at build time.
 *
 * WHAT MAY NEVER GO THROUGH HERE
 * ------------------------------
 * This sits on the path that carries the dispatcher's short-lived Conv-AI
 * session key, so the rules are absolute:
 *
 *   - NEVER the session key / bearer, in any form — not truncated, not a
 *     prefix. `hasSessionKey: true` is the whole answer.
 *   - NEVER the Authorization header object, and never `dispatcher_bff_sid`
 *     (which is httpOnly and unreadable from JS anyway).
 *   - NEVER response rows. A Conv-AI answer carries trip and passenger data;
 *     only its type, row count and length are logged.
 *
 * The question text IS logged — it is what the dispatcher typed, and it is the
 * field that makes a trace readable.
 *
 * Prefix matches the backend's, so one grep spans browser + server logs.
 */

const PREFIX = '[conv-debug]';

/** True only under the dev server AND with the flag explicitly on. */
export const convAiDebugEnabled = (): boolean => {
  try {
    const flag = (import.meta.env.VITE_CONV_AI_DEBUG as string | undefined)?.trim().toLowerCase();
    return Boolean(import.meta.env.DEV) && (flag === 'true' || flag === '1' || flag === 'yes');
  } catch {
    return false;
  }
};

/**
 * Emit one `[conv-debug] <event> { …fields }` line when enabled.
 *
 * Never throws: a failed debug line must not break a dispatcher's message.
 * Pass only values you have already decided are safe — this helper does not
 * and cannot redact for you.
 */
export const convAiDebug = (event: string, fields?: Record<string, unknown>): void => {
  if (!convAiDebugEnabled()) return;
  try {
    if (fields && Object.keys(fields).length > 0) {
      // eslint-disable-next-line no-console
      console.info(`${PREFIX} ${event}`, fields);
    } else {
      // eslint-disable-next-line no-console
      console.info(`${PREFIX} ${event}`);
    }
  } catch {
    /* a debug line is never worth an exception */
  }
};

/**
 * The shape of a Conv-AI response, with no payload in it.
 *
 * Returns the discriminator, the row/column counts for a table, and the length
 * of the text — enough to tell "empty answer", "one record" and "12-row table"
 * apart without putting a single passenger field in the console.
 */
export const describeResponseShape = (response: unknown): Record<string, unknown> => {
  if (response === null || response === undefined) return { shape: 'none' };
  if (typeof response === 'string') return { shape: 'string', chars: response.length };
  if (typeof response !== 'object') return { shape: typeof response };

  const r = response as Record<string, unknown>;
  const out: Record<string, unknown> = { shape: (r.type as string) ?? 'object' };

  if (Array.isArray(r.rows)) out.rows = r.rows.length;
  if (Array.isArray(r.columns)) out.columns = r.columns.length;
  if (Array.isArray(r.data)) out.dataRows = r.data.length;
  if (typeof r.raw === 'string') out.chars = r.raw.length;
  if (typeof r.summary === 'string') out.summaryChars = r.summary.length;
  if (typeof r.title === 'string') out.hasTitle = true;

  return out;
};
